import React, {useRef, useState, useEffect} from 'react'
import {observer} from 'mobx-react'
import {useIntl} from 'react-intl'
import {ArrowContainer, Popover} from 'react-tiny-popover'
import {useBasketStore, useCustomerStore, useGlobalStore} from '../../../../store/hooks/useStore'
import {
  BaseButton,
  Box,
  BoxProps,
  Button,
  Flex,
  Stack,
  Text,
  useDisclosure,
} from '../../../../vanilla'
import {
  CheckIcon,
  ChevronDownIcon,
  CircleStarIcon,
  ClockAlertIcon,
  ExclamationCircleIcon,
} from '../../../../components/icons'
import {BookingConfirmationModal} from '../booking-confirmation-modal'
import {ChangingSlotWarningModal} from '../changing-slot-warning-modal'
import useDeliveryBooking from '../../../../hooks/use-delivery-booking'
import {CredentialModalPlacementMap} from '../../../../types/ui'
import {ALCOHOL_PRODUCT_CLASS} from '../../../../utils/constants'
import {DeliverySlot, ReserveSlotResponse, TimeSlot} from '../../../../types/store/basket'
import * as styles from '../../styles.css'
import {useCredentialModal} from '../../../../contexts'
import {CollapsablePanel} from '../../../../components/collapsable-panel'
import {Link} from '../../../../components/link'
import {BookingSlotErrorModal} from '../booking-slot-error-modal'
import {useSlotExpiration} from '../../../../store/hooks/useSlotExpiration'
import {validateShippingAddress} from '../../../../utils/address-utils'
import {getDeviceType} from '../../../../utils/utils'
import {Countdown} from '../../../../components/book-delivery/reservation-status'
import {slotsMatch} from '../../../../utils/booking-utils'
import useCustomSitePreference from '../../../../hooks/forms/useCustomSitePreference'
import { DeliveryTimeAndDate } from '../../../../components/book-delivery/delivery-time-and-date'
import { baseThemeContract } from '../../../../vanilla/vars.css';

interface TimeSlotsProps {
  selectedTab?: number
  smallBasketEnabled?: boolean
}

const TimeSlotsComponent = ({selectedTab, smallBasketEnabled}: TimeSlotsProps) => {
  const {
    basket,
    currentDeliverySlotId,
    slotTime,
    deliverySlotArray,
    isAppliedPostCodeDeliverable,
    shippingAddress,
    getDeliverySlots
  } = useBasketStore()
  const {isReservationExpired} = useSlotExpiration()

  const {isRegistered, addresses} = useCustomerStore()
  const {reserveSlot} = useDeliveryBooking()

  const confirmationModal = useDisclosure(false)
  const changeSlotWarningModal = useDisclosure(false)
  const errorBookingSlotModal = useDisclosure(false)

  const [selectedSlot, setSelectedSlot] = React.useState<TimeSlot | undefined>()

  const customerHasBookedSlot = Boolean(currentDeliverySlotId)
  const deliveryDay = deliverySlotArray[typeof selectedTab === 'number' ? selectedTab : 1]
  const currentSlotExpiration = slotTime(basket?.c_slotExpiryTime)
  const {showCredentialModal} = useCredentialModal()
  const [bookSlotErrorCode, setBookSlotErrorCode] = useState('success')

  const handleShippingAddressValidation = () => {
    if (!shippingAddress) return
    const shippingAddressValidation = validateShippingAddress(shippingAddress)
    return shippingAddressValidation.isValid
  }
  
  const handleSlotReservation = async (slot: TimeSlot) => {
    const customerAddressSelected = addresses.find(
      (address) => address.addressId === shippingAddress?.c_customerAddressID,
    )

    if (!customerAddressSelected) {
      window.scrollTo({top: 0})
      return false
    }

    setSelectedSlot({
      ...slot,
    })

    if (isRegistered) {
      if (customerHasBookedSlot && isAppliedPostCodeDeliverable && !isReservationExpired()) {
        changeSlotWarningModal.onOpen()
        return
      }

      if (!handleShippingAddressValidation()) {
        setBookSlotErrorCode('300')
        errorBookingSlotModal.onOpen()
        return
      }

      const slotReservation: ReserveSlotResponse = await reserveSlot(slot.id)
      const reservationStatus = slotReservation.result?.code

      if (reservationStatus && reservationStatus === 'success') {
        confirmationModal.onOpen()
      } else {
        if (reservationStatus) {
          setBookSlotErrorCode(reservationStatus)
        }
        errorBookingSlotModal.onOpen()
      }
      return
    }

    const type =
      window.innerWidth < 768
        ? CredentialModalPlacementMap.BOTTOM
        : CredentialModalPlacementMap.ATTACHED

    if (type === CredentialModalPlacementMap.ATTACHED) {
      window.scrollTo({top: 0})
    }

    showCredentialModal(type, async () => {
      if (customerHasBookedSlot && slot.id !== currentDeliverySlotId) {
        changeSlotWarningModal.onOpen()
      } else {
        try {
          await reserveSlot(slot.id)
          confirmationModal.onOpen()
        } catch (error) {
          errorBookingSlotModal.onOpen()
        }
      }
    })
  }

  useEffect(() => {
    if (errorBookingSlotModal.isOpen) {
      // If the slot error message is displayed, we need to refresh the slots
      getDeliverySlots()
    }
  }, [errorBookingSlotModal.isOpen])

  return (
    <Box>
      {deliveryDay?.slots?.map((slot, idx) => (
        <DeliveryDateSlot
          smallBasketEnabled={smallBasketEnabled}
          key={slot.id}
          slot={slot}
          vip={slot.vipSlot || false}
          deliveryDay={deliveryDay}
          currentSlotExpiration={currentSlotExpiration}
          onReservation={handleSlotReservation}
          style={{marginBottom: idx === deliveryDay.slots.length - 1 ? '0px' : '1px'}}
        />
      ))}
      <BookingConfirmationModal
        {...confirmationModal}
        slot={selectedSlot}
        deliveryDay={deliveryDay}
        smallBasketEnabled={smallBasketEnabled}
      />
      <ChangingSlotWarningModal
        {...changeSlotWarningModal}
        newSlot={selectedSlot ?? null}
        onSubmit={async () => {
          if (selectedSlot) {
            try {
              if (handleShippingAddressValidation()) {
                await reserveSlot(selectedSlot.id)
                confirmationModal.onOpen()
              } else {
                errorBookingSlotModal.onOpen()
              }
            } catch (error) {
              errorBookingSlotModal.onOpen()
            }
          }
        }}
      />
      <BookingSlotErrorModal
        {...errorBookingSlotModal}
        statusCode={bookSlotErrorCode}
        isValidShippingAddress={handleShippingAddressValidation()}
      />
    </Box>
  )
}
export const TimeSlots = observer(TimeSlotsComponent)

interface DeliveryDateSlotProps extends BoxProps<React.ElementType> {
  slot: TimeSlot
  deliveryDay: DeliverySlot
  currentSlotExpiration: string
  onReservation: (slot: TimeSlot) => void
  vip: boolean
  smallBasketEnabled?: boolean
}

const DeliveryDateSlotComponent = (props: DeliveryDateSlotProps) => {
  const {
    smallBasketEnabled,
    vip,
    slot,
    onReservation,
    deliveryDay,
    style = {}
  } = props

  const {
    available,
    startHour,
    endHour,
    hasFreeDelivery,
    notReleasedStatus,
    shippingCost,
    restrictedProductClasses,
    sameDayPastCutOff,
    sameDayCutOffDate,
    sameDayCutOffEditDate,
    firstAvailable,
  } = slot

  const {formatNumber} = useIntl()
  const {isRegistered} = useCustomerStore()
  const {currency} = useGlobalStore()
  const {currentDeliverySlotId, hasAlcoholItems, setOpenMiniBasketUI, basket, initMergingUI} = useBasketStore()
  const {isReservationExpired} = useSlotExpiration()
  const popoverRef = useRef<HTMLElement | undefined>(undefined)
  const credentialModal = useCredentialModal()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [pendingSlot, setPendingSlot] = useState<TimeSlot|null>(null)
  const isCurrentBookedSlot = slotsMatch(slot.id,currentDeliverySlotId)
  const isAlcoholProductRestrictedSlot = restrictedProductClasses?.includes(ALCOHOL_PRODUCT_CLASS)
  const isTimeSlotReservationExpired = isReservationExpired() && isCurrentBookedSlot
  const slotNotReleasedPreference = useCustomSitePreference('slotNotReleased', true)

  const baseStyle = {
    backgroundColor:
      !available || (hasAlcoholItems && isAlcoholProductRestrictedSlot) ? '#bebebe' : '#fff',
    minHeight: '60px',
  }

  useEffect(() => {
    if (isRegistered && pendingSlot && !initMergingUI) {
      onReservation(pendingSlot)

      setPendingSlot(null)
    }
  }, [isRegistered, pendingSlot, onReservation, initMergingUI])

  const handleSlotBooking = () => {
    const userAgentString = window.navigator.userAgent
    const deviceType = getDeviceType(userAgentString)
    if (isRegistered) {
      onReservation(slot)
    } else {
      setPendingSlot(slot)
      const modalPlacement =
        deviceType === 'mobile'
          ? CredentialModalPlacementMap.BOTTOM
          : CredentialModalPlacementMap.ATTACHED
      credentialModal.setModalPlacementType(modalPlacement)
      credentialModal.onToggle()
    }
  }

  const getBookActionButtonText = () => {
    const smallBasketChargeText =
      smallBasketEnabled && slot?.smallBasketCharge != null && slot?.smallBasketCharge != 0
        ? ' *'
        : ''

    if (isTimeSlotReservationExpired) {
      return 'Rebook'
    }

    if (hasFreeDelivery || shippingCost === 0) {
      return `Free Delivery${smallBasketChargeText}`
    }

    return `${formatNumber(shippingCost, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}${smallBasketChargeText}`
  }

  const getNonAvailableSlotMessage = () => {
    if (notReleasedStatus && slotNotReleasedPreference) {
        return 'Slot not yet released'
    }

    if (deliveryDay?.isToday && sameDayPastCutOff) {
      return `Same day delivery bookable up until ${sameDayCutOffDate}`
    }

    return 'Sorry this slot is no longer available'
  }

  return (
    <CollapsablePanel
      defaultOpen={false}
      panelContent={
        <TimeSlotMoreInfoContent
          smallBasketEnabled={smallBasketEnabled}
          smallBasketCharge={slot.smallBasketCharge}
          smallBasketChargeThreshold={slot.smallBasketChargeThreshold}
          deliveryCost={shippingCost}
          sameDayCutOffEditTime={sameDayCutOffEditDate}
        />
      }
    >
      {(moreInfoDisclosure) => (
        <Flex
          style={{
            ...baseStyle,
            ...style,
          }}
          paddingX={['16px', '28px']}
          paddingY={['8px', '0px']}
          align="center"
        >
          <Flex style={{width: '75%'}} direction={['column', 'row']} alignItems="center">
            <Stack gap="4px" order="1" style={{marginRight: 'auto'}}>
              <Text data-test-selector="time-slot" >
              <DeliveryTimeAndDate 
                startHour={startHour}
                endHour={endHour}
                textProps={{variant: 'text4',
                  color:
                    isCurrentBookedSlot
                      ? isTimeSlotReservationExpired
                        ? 'promotionColorDark'
                        : 'accent1'
                      : 'gray800'
                }}
              />
              </Text>
              {vip ? (
                <Box className={styles.vipNote}>
                  <CircleStarIcon className={styles.vipToolTipStar} />
                  <Text>VIP Early Access</Text>
                </Box>
              ) : null}
            </Stack>
            {hasAlcoholItems && isAlcoholProductRestrictedSlot && available ? (
              <Flex
                order={['0', '2']}
                width={['full', 'auto']}
                align="center"
                marginX={['0px', 'auto']}
              >
                <Text variant="text4">Restricted slot, please remove alcohol product to book</Text>
              </Flex>
            ) : (
              <>
                <Flex order="2" width={['full', 'auto']} marginX={['0px', 'auto']}>
                  {!available ? (
                    <Text
                      variant="text4"
                      order={['0', '2']}
                      style={{maxWidth: '400px', minWidth: '200px'}}
                    >
                      {getNonAvailableSlotMessage()}
                    </Text>
                  ) : isTimeSlotReservationExpired ? (
                    <Box gap="4px">
                      <ExclamationCircleIcon
                        color="promotionColorDark"
                        boxSize="16px"
                        display="inline-block"
                        marginRight="4px"
                      />
                      <Text
                        variant="text4"
                        color="promotionColorDark"
                        verticalAlign="bottom"
                        display="inline"
                        data-test-selector="delivery-reservation-expired"
                      >
                        Your delivery reservation has expired!
                      </Text>
                    </Box>
                  ) : null}
                </Flex>

                {available && isAlcoholProductRestrictedSlot && (
                  <Flex
                    gap="8px"
                    order={['0', '2']}
                    align="center"
                    width={['full', 'auto']}
                    className={styles.deliveryTimeSlotMessage}
                  >
                    <Text variant="text4" color="promotionColorDark">
                      Alcohol restricted slot
                    </Text>
                    <Popover
                      parentElement={popoverRef.current}
                      content={({position, childRect, popoverRect}) => {
                        return (
                          <ArrowContainer
                            arrowColor="white"
                            arrowSize={10}
                            arrowStyle={{zIndex: baseThemeContract.zIndex.arrowContainer}}
                            childRect={childRect}
                            popoverRect={popoverRect}
                            position={position}
                          >
                            <Box
                              bg="white"
                              display="flex"
                              flexDirection="column"
                              justifyContent="center"
                              padding="16px"
                              borderRadius="base"
                              style={{boxShadow: '0 10px 25px 0 rgb(0 0 0 / 40%)'}}
                              textAlign="center"
                            >
                              <Stack>
                                <Text variant="heading0">Alcohol Licensing Restrictions</Text>
                                <Text variant="text3">
                                  Due to licensing laws, we are unable to deliver alcohol during this selected delivery time.
                                  <br /> 
                                  Availability is determined by local licensing hours and may vary by region.
                                </Text>
                              </Stack>
                            </Box>
                          </ArrowContainer>
                        )
                      }}
                      isOpen={isPopoverOpen}
                      positions={['top']}
                      align="center"
                      reposition={true}
                      onClickOutside={() => setIsPopoverOpen(false)}
                    >
                      <ExclamationCircleIcon
                        color="promotionColorDark"
                        onMouseEnter={() => setIsPopoverOpen(true)}
                        onMouseLeave={() => setIsPopoverOpen(false)}
                      />
                    </Popover>
                  </Flex>
                )}
                {available && firstAvailable && (
                  <Text
                    variant="text4"
                    order={['0', '2']}
                    className={styles.deliveryTimeSlotMessage}
                  >
                    First available delivery time!
                  </Text>
                )}
                {deliveryDay?.isToday && available && (
                  <Flex
                    gap="4px"
                    order="3"
                    align={['flex-start', 'center']}
                    marginTop={['4px', '0px']}
                  >
                    <ClockAlertIcon width="24px" height="auto" marginRight="2px" />
                    <Flex
                      gap="4px"
                      marginTop="4px"
                      direction={['column', 'row']}
                      align={['flex-start', 'center']}
                    >
                      <Text
                        variant="text4"
                        lineHeight="none"
                      >{`Book before ${sameDayCutOffDate}`}</Text>
                      <Flex
                        align="center"
                        gap="4px"
                        cursor="pointer"
                        onClick={() => {
                          moreInfoDisclosure.onToggle()
                        }}
                      >
                        <Text variant="text1" textDecoration="underline" lineHeight="none">
                          More Info
                        </Text>
                        <ChevronDownIcon
                          style={{
                            transform: moreInfoDisclosure.isOpen ? 'rotate(180deg)' : 'unset',
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                )}
              </>
            )}
          </Flex>
          {available ? (
            <Flex
              justifyContent={
                isCurrentBookedSlot && !isTimeSlotReservationExpired ? 'center' : 'flex-end'
              }
              align="center"
              className={styles.deliveryButtonContainer}
              paddingLeft={['8px', '20px']}
            >
              {isAlcoholProductRestrictedSlot && hasAlcoholItems ? (
                <BaseButton
                  size="sm"
                  variant="secondary"
                  maxHeight="40px"
                  style={{width: '240px'}}
                  onClick={() => {
                    setOpenMiniBasketUI(true)
                  }}
                >
                  <Text
                    as="span"
                    variant="unstyled"
                    fontSize="inherit"
                    display={['none', 'inline']}
                  >
                    Edit Basket
                  </Text>
                  <Text
                    as="span"
                    variant="unstyled"
                    fontSize="inherit"
                    display={['inline', 'none']}
                  >
                    Edit
                  </Text>
                </BaseButton>
              ) : (
                <>
                  {isCurrentBookedSlot && !isTimeSlotReservationExpired ? (
                    <Flex align={['flex-start', 'center']} direction={['column', 'column', 'row']} gap="4px" alignItems={'center'}>
                      <Flex direction='row' gap="4px">
                        <CheckIcon color="accent1" width="24px" height="24px" minWidth="24px" />
                        <Text variant="text4" color="accent1" textAlign="center">
                          Booked{' '}
                        </Text>
                      </Flex>
                      <Text color="accent2" variant="text3" textAlign={["center", "left"]}>
                        - Expires in{' '}
                        <Countdown
                          slotExpiration={basket?.c_slotExpiryTime || ''}
                          color="accent2"
                          textVariant="text3"
                        />
                      </Text>
                    </Flex>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      maxHeight="40px"
                      style={{width: '240px'}}
                      onClick={handleSlotBooking}
                      data-test-selector="book-slot"
                    >
                      {getBookActionButtonText()}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          ) : (
            <Box className={styles.unavailableTimeSlotSpacer} />
          )}
        </Flex>
      )}
    </CollapsablePanel>
  )
}

const DeliveryDateSlot = observer(DeliveryDateSlotComponent)

interface TimeSlotMoreInfoContentProps {
  deliveryCost: number
  sameDayCutOffEditTime?: string
  smallBasketEnabled?: boolean
  smallBasketCharge?: number
  smallBasketChargeThreshold?: number
}

const TimeSlotMoreInfoContent = observer((props: TimeSlotMoreInfoContentProps) => {
  const {smallBasketCharge, smallBasketEnabled, sameDayCutOffEditTime, deliveryCost} = props

  const {formatNumber} = useIntl()

  const {customSitePreferences, currency} = useGlobalStore()
  const {minimumBasketValue} = useBasketStore()

  return (
    <Flex
      width="full"
      bg="white"
      padding="24px"
      marginBottom="4px"
      direction={['column', 'row']}
      gap={['16px', '24px']}
    >
      <Box>
        <Text variant="text7" lineHeight="shorter" color="accent0" marginBottom="4px">
          Can I cancel or amend a same day delivery?
        </Text>
        <Text variant="text3">
          {customSitePreferences['disableOrderEditOnSameDay']
            ? 'Once you have placed your Same Day order you won’t be able to edit it'
            : `Yes, you can cancel or amend you order up to ${sameDayCutOffEditTime}`}
        </Text>
      </Box>
      <Box>
        <Text variant="text7" lineHeight="shorter" color="accent0" marginBottom="4px">
          How much is same day delivery?
        </Text>
        <Text variant="text3">
          {deliveryCost
            ? currency &&
              formatNumber(deliveryCost, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : 'Free delivery'}
          {smallBasketEnabled && smallBasketCharge != null && smallBasketCharge != 0 ? '*' : ''}
          {minimumBasketValue
            ? currency &&
              `, ${formatNumber(minimumBasketValue, {
                style: 'currency',
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })} min order value`
            : null}
        </Text>
      </Box>
      <Box>
        <Text
          as={Link}
          variant="text3"
          color={{default: 'accent3', hover: 'accent3Light'}}
          textDecoration="underline"
          cursor="pointer"
          to="/customer-support/contact-us"
        >
          Read FAQs pages
        </Text>
      </Box>
    </Flex>
  )
})
